import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Modal } from './Modal';
import { Button } from './Button';
import { toast } from '@/state/toast';

// 12.3–12.4: находки движка → замечания проектировщику. Предпросмотр с чекбоксами
// (hard отмечены, soft сняты), затем bulk-promote и мост к ГИПу.

interface PItem { ref: string; label: string; hard: boolean; count: number }
interface PGroup { check: string; label: string; hard: boolean; items: PItem[] }
interface Promotable { groups: PGroup[]; hard_total: number; soft_total: number }
interface Member { id: string; email: string; full_name?: string | null; role: string }

export function PromoteFindingsModal({ projectId, onClose, onGoToRemarks }: {
  projectId: string;
  onClose: () => void;
  onGoToRemarks?: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Promotable>({
    queryKey: ['promotable-findings', projectId],
    queryFn: () => api.get<Promotable>(`/projects/${projectId}/promotable-findings`),
  });
  const membersQ = useQuery<{ members: Member[] }>({
    queryKey: ['auth-members', projectId],
    queryFn: () => api.get<{ members: Member[] }>(`/projects/${projectId}/auth-members`),
  });
  const hasDesigner = (membersQ.data?.members || []).some((m) => m.role === 'gip');

  // key = check + '::' + ref; по умолчанию отмечены только hard.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const keyOf = (check: string, ref: string) => `${check}::${ref}`;
  const sel = useMemo(() => {
    if (selected) return selected;
    const s = new Set<string>();
    (data?.groups || []).forEach((g) => g.items.forEach((i) => { if (i.hard) s.add(keyOf(g.check, i.ref)); }));
    return s;
  }, [selected, data]);
  const toggle = (k: string) => {
    const next = new Set(sel);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);
  const [email, setEmail] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');

  const promote = useMutation({
    mutationFn: () => {
      const items = (data?.groups || []).flatMap((g) =>
        g.items.filter((i) => sel.has(keyOf(g.check, i.ref))).map((i) => ({ check: g.check, ref: i.ref })));
      return api.post<{ created: number; skipped: number }>(`/projects/${projectId}/promote-findings`, { items });
    },
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      qc.invalidateQueries({ queryKey: ['remarks-dashboard', projectId] });
      qc.invalidateQueries({ queryKey: ['readiness-summary', projectId] });
      qc.invalidateQueries({ queryKey: ['promotable-findings', projectId] });
    },
    onError: (e) => toast.error((e as Error).message || 'Не удалось создать замечания'),
  });

  const invite = useMutation({
    mutationFn: () => api.post<{ invited: boolean; invite_url?: string }>(`/projects/${projectId}/invite-member`, {
      email: email.trim(), role: 'gip',
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['auth-members', projectId] });
      if (res.invite_url) setInviteUrl(res.invite_url);
      toast.success('Проектировщик приглашён');
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка приглашения'),
  });

  const selCount = sel.size;

  // ── Экран результата ──
  if (result) {
    return (
      <Modal title="Замечания сформированы" onClose={onClose} maxWidth={520}
        footer={<Button variant="primary" onClick={() => { onGoToRemarks?.(); onClose(); }}>Открыть «Замечания»</Button>}>
        <p className="text-ink" style={{ fontSize: 14 }}>
          Создано замечаний: <b>{result.created}</b>
          {result.skipped > 0 && <span className="text-muted"> · пропущено (уже были): {result.skipped}</span>}
        </p>
        {hasDesigner ? (
          <p className="text-muted" style={{ fontSize: 13, marginTop: 12 }}>
            Замечания переданы проектировщику — он видит их в своём кабинете и ответит. Следите за статусом во вкладке «Замечания» → «Ход отработки».
          </p>
        ) : (
          <div className="rounded-card" style={{ marginTop: 16, padding: 14, background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.25)' }}>
            <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>Проектировщик ещё не назначен</div>
            <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Пригласите его — замечания уйдут ему на ответ.</p>
            {inviteUrl ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input readOnly value={inviteUrl} onFocus={(e) => e.currentTarget.select()}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontFamily: 'ui-monospace, monospace' }} />
                <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard.writeText(inviteUrl); toast.success('Скопировано'); }}>Копировать</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email проектировщика"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }} />
                <Button variant="primary" size="sm" disabled={!email.trim() || invite.isPending} onClick={() => invite.mutate()}>
                  {invite.isPending ? 'Отправляем…' : 'Пригласить'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    );
  }

  // ── Экран предпросмотра ──
  return (
    <Modal
      title="Сформировать замечания проектировщику"
      onClose={onClose}
      maxWidth={640}
      footer={
        <div className="flex items-center justify-between" style={{ width: '100%' }}>
          <span className="text-muted" style={{ fontSize: 13 }}>Будет создано: <b className="text-ink">{selCount}</b></span>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Отмена</Button>
            <Button variant="primary" disabled={selCount === 0 || promote.isPending} onClick={() => promote.mutate()}>
              {promote.isPending ? 'Создаём…' : `Создать (${selCount})`}
            </Button>
          </div>
        </div>
      }
    >
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Отмеченные находки станут замечаниями и уйдут проектировщику. Серьёзное отмечено по умолчанию; спорное можно добавить вручную.
      </p>
      {isLoading && <p className="text-muted" style={{ fontSize: 13 }}>Загрузка находок…</p>}
      {!isLoading && (data?.groups || []).length === 0 && (
        <p className="text-muted" style={{ fontSize: 13 }}>Непереданных находок нет — всё уже в замечаниях.</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 420, overflowY: 'auto' }}>
        {(data?.groups || []).map((g) => (
          <div key={g.check}>
            <div className="text-ink" style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{g.label} <span className="text-muted">({g.items.length})</span></div>
            {g.items.map((i) => {
              const k = keyOf(g.check, i.ref);
              return (
                <label key={k} className="flex items-start" style={{ gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sel.has(k)} onChange={() => toggle(k)} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }} className={sel.has(k) ? 'text-ink' : 'text-muted'}>
                    {i.label}
                    {!i.hard && <span className="rounded-pill" style={{ marginLeft: 6, padding: '1px 7px', fontSize: 11, background: '#F3F4F6', color: '#6B7280' }}>спорное</span>}
                  </span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </Modal>
  );
}
